import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useNotif } from '@/context/NotificationContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Users, Plus, ArrowUpRight, Crown, Calendar, Loader2, Wifi, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '@/lib/errors';

function CreateConvoyDialog({ open, onOpenChange, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', description: '', max_size: 12 });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/convoys', { ...form, season: 'summer', max_size: Number(form.max_size) || 12 });
      toast.success(t('convoys.launched'));
      onCreated?.(data);
      onOpenChange(false);
      setForm({ name: '', description: '', max_size: 12 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tighter">{t('convoys.create')}</DialogTitle>
          <DialogDescription className="text-white/60">{t('convoys.subtitle')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-white/80">{t('convoys.form.name')}</Label>
            <Input
              data-testid="convoy-form-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sunset Cruise Tokyo"
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
          <div>
            <Label className="text-white/80">{t('convoys.form.desc')}</Label>
            <Textarea
              data-testid="convoy-form-desc"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="JDM only, cruise tranquille, rdv en ville..."
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30 resize-none"
            />
          </div>
          <div>
            <Label className="text-white/80">{t('convoys.form.max')}</Label>
            <Input
              data-testid="convoy-form-max"
              type="number"
              min={2}
              max={64}
              value={form.max_size}
              onChange={(e) => setForm({ ...form, max_size: e.target.value })}
              className="bg-black/40 border-white/10 text-white"
            />
          </div>
          <Button
            type="submit"
            data-testid="convoy-form-submit"
            disabled={loading || !form.name}
            className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('convoys.form.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Convoys() {
  const { user } = useAuth();
  const { convoyRequests = [] } = useNotif() || {};
  const { t } = useI18n();
  const [convoys, setConvoys] = useState([]);
  const [online, setOnline] = useState({ online: [], count: 0 });
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    try {
      const [c, o] = await Promise.all([api.get('/convoys'), api.get('/presence/online')]);
      setConvoys(c.data);
      setOnline(o.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t1 = setInterval(load, 15000);
    return () => clearInterval(t1);
  }, []);

  // Heartbeat
  useEffect(() => {
    if (!user || user === false) return;
    const beat = () => api.post('/presence/heartbeat').catch(() => {});
    beat();
    const i = setInterval(beat, 30000);
    return () => clearInterval(i);
  }, [user]);

  const join = async (c) => {
    if (!user || user === false) { toast.message(t('convoys.must_login_join')); return; }
    try {
      const { data } = await api.post(`/convoys/${c.id}/join`);
      setConvoys((prev) => prev.map((x) => x.id === c.id ? data : x));
      if (data.pending_join) {
        toast.success(t('convoys.request_sent') || 'Demande envoyée à l\'hôte');
      } else {
        toast.success(t('convoys.joined_toast'));
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 bg-[#0A0A0A] text-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF] text-xs uppercase tracking-[0.25em] font-bold">
              <Wifi className="w-3 h-3 animate-pulse" /> {online.count} {t('convoys.online')}
            </div>
            <h1 className="font-display text-4xl lg:text-6xl font-black uppercase tracking-tighter mt-3">
              {t('convoys.title')}
            </h1>
            <p className="text-white/60 mt-2 max-w-xl">{t('convoys.subtitle')}</p>
          </div>
          <Button
            data-testid="convoy-create-btn"
            onClick={() => user && user !== false ? setCreateOpen(true) : toast.message(t('convoys.must_login_create'))}
            className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide h-12 px-6"
          >
            <Plus className="w-4 h-4 mr-2" /> {t('convoys.create')}
          </Button>
        </div>

        {/* Pending join requests banner (host only) */}
        {convoyRequests.length > 0 && (
          <div className="mt-6 glass rounded-2xl p-4 border border-[#FFD479]/40 bg-[#FFD479]/5" data-testid="pending-requests-banner">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-[#FFD479] animate-pulse" />
              <div className="text-xs uppercase tracking-[0.2em] text-[#FFD479] font-bold">
                {t('convoys.pending_requests') || 'Demandes en attente'}
              </div>
            </div>
            <div className="space-y-2">
              {convoyRequests.map((cv) => (
                <Link
                  key={cv.convoy_id}
                  to={`/convoys/${cv.convoy_id}`}
                  data-testid={`pending-request-link-${cv.convoy_id}`}
                  className="flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 rounded-xl px-3 py-2 transition group"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Users className="w-4 h-4 text-[#FFD479] shrink-0" />
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{cv.convoy_name}</div>
                      <div className="text-[11px] text-white/60 truncate">
                        {(cv.requests || []).slice(0, 3).map((r) => r.name).join(', ')}
                        {(cv.requests?.length || 0) > 3 && ` +${cv.requests.length - 3}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="px-2 py-1 rounded-full bg-[#FFD479] text-black text-[10px] font-black">
                      {cv.requests?.length || 0}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-white/60 group-hover:text-[#FFD479] transition" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Convoys grid */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {convoys.map((c) => {
            const isMember = user && user !== false && c.members.some((m) => m.user_id === user.id);
            const isFull = c.members_count >= c.max_size;
            const hasPending = user && user !== false && (c.join_requests || []).some((r) => r.user_id === user.id);
            return (
              <div key={c.id} data-testid={`convoy-card-${c.id}`} className="glass rounded-2xl overflow-hidden flex flex-col">
                <div className="p-4 flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-black text-xl uppercase tracking-tighter truncate">{c.name}</h3>
                    <div className="text-xs text-white/50 mt-1 inline-flex items-center gap-1">
                      <Crown className="w-3 h-3 text-[#FFD479]" /> {c.host_name}
                    </div>
                  </div>
                  <Link
                    to={`/convoys/${c.id}`}
                    data-testid={`convoy-open-${c.id}`}
                    className="text-white/40 hover:text-white"
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </Link>
                </div>

                {c.description && (
                  <p className="px-4 text-sm text-white/70 line-clamp-2">{c.description}</p>
                )}

                {c.scheduled_at && (
                  <div className="px-4 mt-2 text-xs text-white/50 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {t('convoys.scheduled')}: {new Date(c.scheduled_at).toLocaleString()}
                  </div>
                )}

                {/* Members avatars */}
                <div className="px-4 mt-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-white/40" />
                  <div className="flex -space-x-2">
                    {c.members.slice(0, 6).map((m) => (
                      <div key={m.user_id} className="w-7 h-7 rounded-full bg-[#00F0FF] text-black border-2 border-black grid place-items-center text-[10px] font-bold" title={m.name}>
                        {m.avatar_url ? <img src={m.avatar_url} alt={m.name} className="w-full h-full rounded-full" /> : m.name[0]?.toUpperCase()}
                      </div>
                    ))}
                  </div>
                  <span className="text-xs text-white/50">{c.members_count} / {c.max_size}</span>
                </div>

                {/* Actions */}
                <div className="p-4 mt-auto flex gap-2">
                  <Button
                    data-testid={`convoy-join-${c.id}`}
                    onClick={() => (isMember || hasPending ? null : join(c))}
                    disabled={(isFull && !isMember) || hasPending}
                    className={`flex-1 rounded-full font-bold uppercase tracking-wide ${
                      isMember
                        ? 'bg-white text-black hover:bg-white/90'
                        : hasPending
                          ? 'bg-[#FFD479]/20 border border-[#FFD479]/40 text-[#FFD479]'
                          : 'bg-[#FF007F] hover:bg-[#FF007F]/90 text-white'
                    }`}
                  >
                    {isMember
                      ? '✓ ' + t('convoys.join')
                      : hasPending
                        ? t('convoys.pending_approval') || 'Demande envoyée'
                        : (isFull ? t('convoys.full') : t('convoys.request_join') || t('convoys.join'))}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {convoys.length === 0 && (
          <div className="mt-12 glass rounded-2xl p-10 text-center">
            <Users className="w-8 h-8 mx-auto text-white/30" />
            <div className="text-white/70 font-display font-bold text-lg mt-3">{t('convoys.empty')}</div>
          </div>
        )}
      </div>

      <CreateConvoyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(c) => setConvoys((prev) => [c, ...prev])}
      />
    </div>
  );
}
